import { Product } from '@dfds-frontend/tracking'
import { mapUnloRoute } from '@dfds-pax/utils'

import { Departure } from '@/states/selection.interface'

import getCookieConsent from '@/utils/getCookieConsent'
import getGaClientId from '@/utils/getGaClientId'
import { getUserSegmentId } from '@/utils/getUserSegmentId'
import { getSessionBasket } from '@/utils/sessionStorage'

import { TrackingData } from '../_actions/getTrackingData'
import { CartTrackingInput } from '../_actions/trackCartSegment'

// Jotai-free cart payload: sourced from the basket-derived trackingData + the tracking Product so the
// Segment events don't read selection atoms. petCount is supplied by the caller.
const buildCartPayloadFromTracking = (
  leg: 'out' | 'home',
  trackingData: TrackingData,
  product: Product,
  petCount: number,
  currency?: string
): CartTrackingInput | null => {
  const anonymousUserId = getUserSegmentId()
  if (!anonymousUserId) return null

  const departure = leg === 'out' ? trackingData.departures[0] : trackingData.departures[1]
  if (!departure) return null

  // Foot-passenger baskets have no vehicle; fall back to NCAR to match the selection-based payload (see _ecommerce/products.ts).
  const vehicle = trackingData.trailerCode
    ? `${trackingData.vehicleCode ?? 'NCAR'}|${trackingData.trailerCode}`
    : (trackingData.vehicleCode ?? 'NCAR')

  return {
    adults: trackingData.numberAdults,
    anonymousUserId,
    arrivalPortCode: departure.arrivalPort,
    brand: product.brand,
    cartId: trackingData.basketId,
    childrens: trackingData.agesOfChildren,
    cookie: getCookieConsent(),
    currency: currency ?? trackingData.currency,
    departureDate: departure.departureDate,
    departurePortCode: departure.departurePort,
    departureTime: departure.departureTime ?? '',
    gaClientId: getGaClientId(),
    leg,
    path: window.location.pathname,
    petCount,
    position: 0,
    price: product.price,
    product: product.name,
    product_id: product.id,
    productCode: departure.productCode ?? '',
    quantity: product.quantity,
    referrer: document.referrer,
    route: mapUnloRoute(`${departure.departurePort}-${departure.arrivalPort}`),
    search: window.location.search,
    title: document.title,
    tripType: trackingData.departures.length === 2 ? 'return' : 'oneway',
    url: document.URL,
    vehicle,
  }
}

// Total pets in the basket, read from the outbound leg's PET add-ons (a pet selection applies to both legs).
const countPetsFromTracking = (trackingData: TrackingData): number =>
  (trackingData.departures[0]?.addons ?? [])
    .filter((addon) => addon.parentAddonCode?.toUpperCase() === 'PET')
    .reduce((total, addon) => total + addon.quantity, 0)

// petCount is the post-change total supplied by the caller (the pets page mutates it live).
export const buildPetCartPayload = (
  leg: 'out' | 'home',
  trackingData: TrackingData,
  product: Product,
  petCount: number,
  currency?: string
): CartTrackingInput | null => buildCartPayloadFromTracking(leg, trackingData, product, petCount, currency)

// Add-on changes don't affect pet count, so derive it from the basket-backed trackingData.
export const buildAddonCartPayload = (
  leg: 'out' | 'home',
  trackingData: TrackingData,
  product: Product,
  currency?: string
): CartTrackingInput | null => buildCartPayloadFromTracking(leg, trackingData, product, countPetsFromTracking(trackingData), currency)

export interface DepartureTrackingContext {
  adults: number
  childrenAges: number[]
  isReturnBooking: boolean
  petCount: number
  trailerCode?: string
  vehicleCode: string
}

// Departures have no basket-derived trackingData at selection time, so the payload is built from the
// selected Departure + a caller-supplied context instead of the selection atoms.
export const buildDepartureCartPayload = (
  leg: 'out' | 'home',
  departure: Departure,
  context: DepartureTrackingContext
): CartTrackingInput | null => {
  const anonymousUserId = getUserSegmentId()
  if (!anonymousUserId) return null

  const vehicle = context.trailerCode ? `${context.vehicleCode}|${context.trailerCode}` : context.vehicleCode

  return {
    adults: context.adults,
    anonymousUserId,
    arrivalPortCode: departure.arrivalPortCode,
    brand: 'SeabookDeparture',
    cartId: getSessionBasket().basketId,
    childrens: context.childrenAges,
    cookie: getCookieConsent(),
    currency: departure.currencyCode,
    departureDate: departure.departureDate,
    departurePortCode: departure.departurePortCode,
    departureTime: departure.departureTime,
    gaClientId: getGaClientId(),
    leg,
    path: window.location.pathname,
    petCount: context.petCount,
    position: departure.listPosition,
    price: departure.departurePrice,
    product: vehicle,
    product_id: vehicle,
    productCode: departure.productCode,
    quantity: 1,
    referrer: document.referrer,
    route: mapUnloRoute(`${departure.departurePortCode}-${departure.arrivalPortCode}`),
    search: window.location.search,
    title: document.title,
    tripType: context.isReturnBooking ? 'return' : 'oneway',
    url: document.URL,
    vehicle,
  }
}

// The basket departure fields an add-on removal needs, kept as primitives so this stays decoupled from the BFF basket type.
export interface AddonRemovalDeparture {
  arrivalPort: string
  currency: string
  departureDate: string
  departurePort: string
  departureTime: string
  offerCode?: string
  productCode: string
}

// Removing add-ons on yourbooking has no trackingData, so the payload is built from the basket departure
// + the add-on Product + a caller-supplied context (same approach as departures).
export const buildAddonRemovalCartPayload = (
  leg: 'out' | 'home',
  departure: AddonRemovalDeparture,
  product: Product,
  context: DepartureTrackingContext
): CartTrackingInput | null => {
  const anonymousUserId = getUserSegmentId()
  if (!anonymousUserId) return null

  const vehicle = context.trailerCode ? `${context.vehicleCode}|${context.trailerCode}` : context.vehicleCode

  return {
    adults: context.adults,
    anonymousUserId,
    arrivalPortCode: departure.arrivalPort,
    brand: product.brand,
    cartId: getSessionBasket().basketId,
    childrens: context.childrenAges,
    cookie: getCookieConsent(),
    currency: departure.currency,
    departureDate: departure.departureDate,
    departurePortCode: departure.departurePort,
    departureTime: departure.departureTime,
    gaClientId: getGaClientId(),
    leg,
    path: window.location.pathname,
    petCount: context.petCount,
    position: 0,
    price: product.price,
    product: product.name,
    product_id: product.id,
    productCode: departure.productCode,
    quantity: product.quantity,
    referrer: document.referrer,
    route: mapUnloRoute(`${departure.departurePort}-${departure.arrivalPort}`),
    search: window.location.search,
    title: document.title,
    tripType: context.isReturnBooking ? 'return' : 'oneway',
    url: document.URL,
    vehicle,
  }
}
